import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  listExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  getExpensesWorkspace,
  getExpenseAnalytics,
  listExpenseCategories,
  createExpenseCategory,
  listManagedExpenseCategories,
  createManagedExpenseCategory,
  renameManagedExpenseCategory,
  deleteManagedExpenseCategory,
} from '@/api/services/expenses.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseListQuery,
  ExpenseWorkspaceQuery,
  ExpenseAnalyticsQuery,
  ExpenseCategoryRecord,
} from '@/features/expenses/types/expense.types'

export const expenseKeys = {
  all: (companyId: string | null) => ['expenses', companyId] as const,
  list: (companyId: string | null, params: ExpenseListQuery) =>
    ['expenses', companyId, 'list', params] as const,
  workspace: (params: ExpenseWorkspaceQuery | undefined) =>
    ['expenses', 'workspace', params] as const,
  analytics: (params: ExpenseAnalyticsQuery) => ['expenses', 'analytics', params] as const,
  categories: () => ['expenses', 'categories'] as const,
}

export function useExpensesWorkspace(
  params: ExpenseWorkspaceQuery | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: expenseKeys.workspace(params),
    queryFn: () => getExpensesWorkspace(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useExpenseList(
  companyId: string | null,
  params: ExpenseListQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: expenseKeys.list(companyId, params),
    queryFn: () => listExpenses(companyId!, params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && options?.enabled !== false,
  })
}

export function useExpenseAnalytics(
  params: ExpenseAnalyticsQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: expenseKeys.analytics(params),
    queryFn: () => getExpenseAnalytics(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useExpenseCategories(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: expenseKeys.categories(),
    queryFn: listExpenseCategories,
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useCreateExpense() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: string | null; body: CreateExpenseDto }) =>
      createExpense(companyId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['expenses', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: ['expenses', 'analytics'] })
      toast.success('Expense added.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateExpense() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      id,
      body,
    }: {
      companyId: string | null
      id: string
      body: UpdateExpenseDto
    }) => updateExpense(companyId, id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['expenses', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: ['expenses', 'analytics'] })
      toast.success('Expense updated.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteExpense() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, id }: { companyId: string | null; id: string }) =>
      deleteExpense(companyId, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['expenses', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: ['expenses', 'analytics'] })
      toast.success('Expense deleted.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useCreateExpenseCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name }: { name: string }) => createExpenseCategory(name),
    onSuccess: (created) => {
      // Reflect the new option immediately in every open category select. Waiting
      // only for invalidation/refetch can leave the modal showing stale data for
      // the full stale-time window (or indefinitely while offline).
      queryClient.setQueryData<ExpenseCategoryRecord[]>(
        expenseKeys.categories(),
        (current = []) => {
          const withoutDuplicate = current.filter(
            (category) => category._id !== created._id && category.name !== created.name,
          )
          return [...withoutDuplicate, created].sort((a, b) => a.name.localeCompare(b.name))
        },
      )
      queryClient.invalidateQueries({ queryKey: expenseKeys.categories() })
      toast.success('Category added.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useManagedExpenseCategories() {
  return useQuery({
    queryKey: ['expenses', 'category-management'],
    queryFn: listManagedExpenseCategories,
  })
}

export function useCreateManagedExpenseCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createManagedExpenseCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      toast.success('Expense category added')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useRenameExpenseCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      renameManagedExpenseCategory(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      toast.success('Expense category renamed')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useDeleteExpenseCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteManagedExpenseCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      toast.success('Expense category deleted')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
